import React from "react";
import { Link } from "react-router-dom";

import Seo from "../seo/Seo";
import {
  organizationSchema,
  webPageSchema,
  breadcrumbSchema,
  faqSchema,
} from "../seo/schema";
import PageHeader from "../components/PageHeader";
import Breadcrumbs from "../components/Breadcrumbs";
import { GAMES_BY_TIME } from "../data/games";
import { useNowIST } from "../lib/useResults";

const PATH = "/faq";

const FAQS = [
  {
    q: "What is Bagad Satta Company?",
    a: "Bagad Satta Company is a free result and chart website. It publishes the daily declared number for ten games and keeps a permanent, searchable record of every past result from 2024 onwards.",
  },
  {
    q: "How often are the results updated?",
    a: "Continuously. Each game has a fixed declaration time and the page refreshes its data on its own — once a game's time passes, its number replaces the XX placeholder without you refreshing the page.",
  },
  {
    q: "What does XX mean in the result table?",
    a: "XX is a placeholder. It means the number for that game and date has not been declared yet, or that no number was published for that date. It is never a result value.",
  },
  {
    q: "What are the result times for all games?",
    a: "Disawar 5:20 AM, Bagad 1:30 PM, Delhi Bazar 3:15 PM, Shri Ganesh 4:45 PM, Faridabad 6:15 PM, Jai Bhawani 7:15 PM, Ghaziabad 9:30 PM, Jai Hanuman 10:20 PM, Gogamedi 11:30 PM and Gali 11:30 PM. All times are IST.",
  },
  {
    q: "How far back do the charts go?",
    a: "Records are kept for 2024, 2025 and the current year. Use the year and month selectors on the chart page to open any month in that range.",
  },
  {
    q: "Do I need to register or pay to see results?",
    a: "No. Every result and every chart on this website is free to view and needs no account, no login and no payment.",
  },
  {
    q: "What is the difference between a PRO and a MAX account in the app?",
    a: "A PRO account pays a 10 → 950 jodi rate and a 100 → 950 haruf rate, with 1% commission on every deposit. A MAX account pays a 10 → 900 jodi rate and a 100 → 900 haruf rate, with 5% commission on every deposit. Both have a minimum deposit of ₹30 and both earn 1% lifetime referral commission.",
  },
  {
    q: "Which account should I choose, PRO or MAX?",
    a: "PRO gives the higher winning rate (950) with a smaller 1% deposit commission. MAX gives a lower winning rate (900) but a much larger 5% commission on every deposit. PRO favours the rate; MAX favours the deposit bonus.",
  },
  {
    q: "What is the minimum deposit?",
    a: "₹30, on both PRO and MAX accounts.",
  },
  {
    q: "Is the referral commission a one-time payment?",
    a: "No. Referral commission is 1% for the lifetime of the referred account, on both PRO and MAX.",
  },
  {
    q: "Is this website affiliated with any satta matka company?",
    a: "No. This website is published for informational purposes only and is not associated with any satta matka company or gambling organisation.",
  },
  {
    q: "Is satta legal in India?",
    a: "Satta and gambling are illegal in many Indian states and in many other countries. Laws differ by state. Please check and follow the law in your own region. We do not encourage or promote gambling in any form.",
  },
];

const Faq = () => {
  const nowIST = useNowIST();
  const title =
    "Satta Result FAQ — Timings, Charts, PRO & MAX Accounts | Bagad Satta Company";
  const description =
    "Answers to the most common questions about satta results: game timings, what XX means, how far back the charts go, and the difference between PRO and MAX accounts.";

  const trail = [
    { name: "Home", path: "/" },
    { name: "FAQ", path: PATH },
  ];

  return (
    <>
      <Seo
        path={PATH}
        title={title}
        description={description}
        keywords="satta result faq, satta result time, satta chart help, pro vs max account, bagad satta company faq"
        schema={[
          organizationSchema(),
          webPageSchema({ path: PATH, title, description }),
          breadcrumbSchema(trail),
          faqSchema(FAQS),
        ]}
      />

      <div className="page-banner">
        <div className="container page-padding">
          <Breadcrumbs trail={trail} />

          <PageHeader
            h1="Frequently Asked Questions"
            intro="Everything people ask most often about result timings, charts and the PRO and MAX account types."
            nowIST={nowIST}
          />

          <section className="card text-content">
            <dl className="faq-list">
              {FAQS.map((faq) => (
                <div className="faq-item" key={faq.q}>
                  <dt>{faq.q}</dt>
                  <dd>{faq.a}</dd>
                </div>
              ))}
            </dl>

            <h2>Result Times at a Glance</h2>
            <ul className="related-links">
              {GAMES_BY_TIME.map((game) => (
                <li key={game.key}>
                  <Link to={`/${game.slug}`}>
                    {game.name} — {game.timeLabel}
                  </Link>
                </li>
              ))}
            </ul>

            <h2>Still Need Help?</h2>
            <p>
              Write to us from the <Link to="/contact-us">contact page</Link> and
              we will reply. You may also want to read our{" "}
              <Link to="/disclaimer">disclaimer</Link> and{" "}
              <Link to="/terms-and-conditions">terms and conditions</Link>.
            </p>
          </section>
        </div>
      </div>
    </>
  );
};

export default Faq;
